import { useMutation } from '@tanstack/react-query';
import { ImagePlus, Loader2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import { segmentorApi, type SegmentResponse } from '../../services/segmentorApi';

const SegmentorView = () => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const segmentMutation = useMutation({
    mutationFn: (file: File) => segmentorApi.segment(file),
  });

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFile = (file: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    setFilename(file.name);
    segmentMutation.mutate(file);
  };

  const result = segmentMutation.data;

  return (
    <>
      <PageHeader
        title="AI Segmentor"
        subtitle="Upload a waste image — model returns instance segmentation masks and waste-type classifications."
        actions={
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md bg-tfs-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Upload className="size-4" aria-hidden /> Upload image
          </button>
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />

      {result?.mock && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          ⚠ Showing <strong>mock detections</strong>. The real Detectron2 inference service ships in
          phase 5.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2" title={filename ? `Preview · ${filename}` : 'Upload preview'}>
          {previewUrl ? (
            <div className="relative inline-block max-w-full">
              <img
                src={previewUrl}
                alt="Uploaded waste"
                className="block max-h-[520px] max-w-full rounded-md"
              />
              {result && (
                <DetectionOverlay detections={result.detections} />
              )}
              {segmentMutation.isPending && (
                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-slate-900/40 text-white">
                  <Loader2 className="size-5 animate-spin" />
                  <span className="ml-2 text-sm">Segmenting…</span>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-72 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 text-slate-500 hover:border-tfs-primary hover:text-tfs-primary"
            >
              <ImagePlus className="size-10" aria-hidden />
              <span className="text-sm font-medium">Click to choose an image</span>
              <span className="text-xs text-slate-400">PNG, JPG up to 10 MB</span>
            </button>
          )}
        </Card>

        <div className="flex flex-col gap-6">
          <Card title="Detections">
            {segmentMutation.isError && (
              <p className="text-sm text-red-600">
                Failed: {(segmentMutation.error as Error)?.message ?? 'Unknown error'}
              </p>
            )}
            {!result && !segmentMutation.isPending && (
              <p className="py-2 text-sm text-slate-400">Upload an image to see detections.</p>
            )}
            {result && (
              <ul className="flex flex-col gap-2">
                {result.detections.map((d, i) => (
                  <li
                    key={`${d.label}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block size-2.5 rounded-full"
                        style={{ background: d.color }}
                      />
                      <span className="text-sm font-medium text-slate-800">{d.label}</span>
                    </span>
                    <span className="font-mono text-xs text-slate-500">
                      {(d.confidence * 100).toFixed(1)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {result && Object.keys(result.summary).length > 0 && (
            <Card title="Summary">
              <ul className="flex flex-col gap-1.5 text-sm">
                {Object.entries(result.summary)
                  .sort(([, a], [, b]) => b - a)
                  .map(([label, count]) => (
                    <li key={label} className="flex justify-between">
                      <span className="text-slate-700">{label}</span>
                      <span className="font-mono text-slate-500">×{count}</span>
                    </li>
                  ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

const DetectionOverlay = ({ detections }: { detections: SegmentResponse['detections'] }) => (
  <svg
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    className="pointer-events-none absolute inset-0 size-full"
  >
    {detections.map((d, i) => (
      <g key={i}>
        <rect
          x={d.bbox[0] * 100}
          y={d.bbox[1] * 100}
          width={d.bbox[2] * 100}
          height={d.bbox[3] * 100}
          fill={d.color}
          fillOpacity={0.18}
          stroke={d.color}
          strokeWidth={0.4}
        />
        <text
          x={d.bbox[0] * 100 + 0.5}
          y={d.bbox[1] * 100 + 2.2}
          fontSize={1.6}
          fill="#fff"
          stroke={d.color}
          strokeWidth={0.15}
          paintOrder="stroke"
          fontWeight={600}
        >
          {d.label} {Math.round(d.confidence * 100)}%
        </text>
      </g>
    ))}
  </svg>
);

export default SegmentorView;
